import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Divider, Skeleton } from '@mantine/core';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CircleCheck,
  Clock
} from 'tabler-icons-react';
import { Context as RegistrationContext } from '../../../providers/RegistrationProvider';
import AppTitle from '../../common/AppTitle';
import HeaderView from '../../common/HeaderView';
import AppCard from '../../common/AppCard';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import PaymentPlanInstallmentTable from './PaymentPlanInstallmentTable';
import PaymentPlanOrderSummary from './PaymentPlanOrderSummary';
import UpdatePaymentMethodModal from './UpdatePaymentMethodModal';
import { useMediaQueryIndex } from '../../../helpers/hooks';
import { getResponsiveStyle as rs } from '../../../helpers/styles';
import { currencyFormat } from '../../../helpers/format';
import {
  getPaymentPlanSummary,
  PROCESSING_COLOR
} from '../../../helpers/customerPaymentsHelper';

const getPaymentReturnParams = (search) => {
  const params = new URLSearchParams(search);
  let secureId = null;
  let paymentStatus = null;
  params.forEach((value, key) => {
    const lowerKey = key.toLowerCase();
    if (lowerKey === 'secureid') {
      secureId = value;
    }
    else if (lowerKey === 'status') {
      paymentStatus = value.toLowerCase();
    }
  });
  return { paymentStatus, secureId };
};

const PaymentPlanDetails = () => {
  const mqIndex = useMediaQueryIndex();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const hasFetched = useRef(false);
  const returnParams = useRef(getPaymentReturnParams(search));
  const requestedPlanKey = useRef(null);
  const { pkRegCart, pkRegCartPayment } = useParams();
  const [modalState, setModalState] = useState({ isOpen: false });
  const [cardUpdateResult, setCardUpdateResult] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const [orderState, setOrderState] = useState({
    regCart: null,
    failed: false
  });
  const {
    state,
    fetchCartPaymentOrder,
    fetchCustomerPayments,
    refreshCartPaymentPlan
  } = useContext(RegistrationContext);

  const orderRegCart =
    !orderState.failed &&
    orderState.regCart?.pkRegCart?.toString() === pkRegCart?.toString()
      ? orderState.regCart
      : null;
  const isOrderLoading = !orderState.failed && !orderRegCart;

  const listPlan =
    state.customerPayments.value?.paymentPlans?.find(
      (p) =>
        p.pkRegCartPayment?.toString() === pkRegCartPayment?.toString() &&
        p.pkRegCart?.toString() === pkRegCart?.toString()
    ) ?? null;
  const refreshedPlan =
    !refreshError &&
    state.regCartPaymentPlan.value?.pkRegCartPayment?.toString() ===
      pkRegCartPayment?.toString() &&
    state.regCartPaymentPlan.value?.pkRegCart?.toString() ===
      pkRegCart?.toString()
      ? state.regCartPaymentPlan.value
      : null;
  const plan = refreshedPlan ?? listPlan;
  const summary = plan ? getPaymentPlanSummary(plan) : null;
  const isLoading =
    !hasFetched.current ||
    (!plan &&
      (state.customerPayments.loading || state.regCartPaymentPlan.loading));

  const summaryRows = plan
    ? [
        {
          label: 'Plan total',
          value: currencyFormat(plan.amountInCents / 100)
        },
        {
          label: 'Paid',
          value: currencyFormat(plan.paidInCents / 100)
        },
        {
          label: 'Remaining',
          value: currencyFormat(summary.outstandingInCents / 100),
          color: plan.failedInCents > 0 ? '#C40000' : undefined
        },
        ...(plan.refundedInCents > 0
          ? [
              {
                label: 'Refunded',
                value: currencyFormat(plan.refundedInCents / 100)
              }
            ]
          : []),
        {
          label: 'Next payment',
          value: summary.nextDueLabel ?? '—'
        },
        ...(plan.cardMask
          ? [{ label: 'Latest card used', value: `•••• ${plan.cardMask}` }]
          : [])
      ]
    : [];

  useEffect(() => {
    const { paymentStatus } = returnParams.current;
    if (paymentStatus) {
      setCardUpdateResult(
        paymentStatus === 'success' || paymentStatus === '5000'
          ? 'success'
          : 'failed'
      );
      navigate(pathname, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (pkRegCart && pkRegCartPayment) {
      const planKey = `${pkRegCart}-${pkRegCartPayment}`;
      const { secureId } = returnParams.current;
      requestedPlanKey.current = planKey;
      returnParams.current = { ...returnParams.current, secureId: null };
      setRefreshError(null);
      setOrderState({ regCart: null, failed: false });
      fetchCustomerPayments(true);
      refreshCartPaymentPlan(
        pkRegCart,
        pkRegCartPayment,
        secureId,
        null,
        (error, type, data, status) => {
          if (requestedPlanKey.current === planKey) {
            setRefreshError({ status });
          }
        }
      );
      fetchCartPaymentOrder(
        pkRegCart,
        (regCart) => {
          if (requestedPlanKey.current === planKey) {
            setOrderState({ regCart, failed: false });
          }
        },
        () => {
          if (requestedPlanKey.current === planKey) {
            setOrderState({ regCart: null, failed: true });
          }
        }
      );
      hasFetched.current = true;
    }
  }, [pkRegCart, pkRegCartPayment]);

  return (
    <HeaderView
      maxViewWidth={1100}
      viewProps={{
        style: {
          gap: rs([10, 10, 16], mqIndex),
          marginBottom: rs([10, 10, 40], mqIndex)
        }
      }}
    >
      <UpdatePaymentMethodModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ isOpen: false })}
        pkRegCart={pkRegCart}
        pkRegCartPayment={pkRegCartPayment}
      />

      <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
        <ArrowLeft color="dodgerblue" size={18} />
        <AppText
          component={Link}
          style={{ fontSize: 14, textDecoration: 'none', color: 'dodgerblue' }}
          to="/payments"
          weight={500}
        >
          Back to payments
        </AppText>
      </AppFlexbox>

      {isLoading ? (
        <>
          <Skeleton height={36} width={250} />
          <AppCard style={{ border: 'solid 1px #ced4da' }}>
            <AppStack style={{ gap: 12 }}>
              <Skeleton height={16} width={200} />
              <Divider />
              <PaymentPlanInstallmentTable isLoading />
            </AppStack>
          </AppCard>
        </>
      ) : !plan ? (
        <AppCard style={{ border: 'solid 1px #ced4da' }}>
          <AppFlexbox
            style={{
              alignSelf: 'center',
              justifyContent: 'center',
              padding: 40,
              gap: 10
            }}
          >
            <AppText style={{ fontSize: 16 }}>
              {refreshError && refreshError.status !== 404
                ? "We couldn't load this payment plan right now. Please try again shortly."
                : 'This payment plan could not be found.'}
            </AppText>
          </AppFlexbox>
        </AppCard>
      ) : (
        <>
          <AppFlexbox
            style={{
              gap: 12,
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap'
            }}
          >
            <AppStack style={{ gap: 0 }}>
              <AppTitle order={3}>
                {plan.associationName ?? 'Payment plan'}
              </AppTitle>
              <AppText style={{ fontSize: 13, color: '#666' }}>
                Order # {plan.pkRegCart}
              </AppText>
            </AppStack>
            <Badge color={summary.statusColor} size="lg" variant="light">
              {summary.statusLabel}
            </Badge>
          </AppFlexbox>

          {cardUpdateResult === 'success' ? (
            <Alert
              color="green"
              icon={<CircleCheck style={{ width: 24, height: 24 }} />}
              p="xs"
              variant="light"
            >
              <AppStack style={{ gap: 2 }}>
                <AppText style={{ fontSize: 14 }} weight={500}>
                  Your new card has been saved.
                </AppText>
                <AppText style={{ fontSize: 13, color: '#666' }}>
                  Your upcoming scheduled payments will use this card.
                </AppText>
              </AppStack>
            </Alert>
          ) : (
            cardUpdateResult === 'failed' && (
              <Alert
                color="red"
                icon={<AlertTriangle style={{ width: 24, height: 24 }} />}
                p="xs"
                variant="light"
              >
                <AppStack style={{ gap: 2 }}>
                  <AppText style={{ fontSize: 14 }} weight={500}>
                    Your card wasn&apos;t updated.
                  </AppText>
                  <AppText style={{ fontSize: 13, color: '#666' }}>
                    No changes were made to your payment plan. You can try again
                    below.
                  </AppText>
                </AppStack>
              </Alert>
            )
          )}

          {summary.needsCardUpdate ? (
            <Alert
              color="red"
              icon={<AlertTriangle style={{ width: 24, height: 24 }} />}
              p="xs"
              variant="light"
            >
              <AppText style={{ fontSize: 14 }} weight={500}>
                A payment failed. New card details are needed.
              </AppText>
            </Alert>
          ) : (
            summary.isProcessing && (
              <Alert
                color={PROCESSING_COLOR}
                icon={<Clock style={{ width: 24, height: 24 }} />}
                p="xs"
                variant="light"
              >
                <AppStack style={{ gap: 2 }}>
                  <AppText style={{ fontSize: 14 }} weight={500}>
                    A scheduled payment is still processing.
                  </AppText>
                  <AppText style={{ fontSize: 13, color: '#666' }}>
                    It can take a few days for a payment to be confirmed. No
                    action is needed right now.
                  </AppText>
                </AppStack>
              </Alert>
            )
          )}

          <AppCard style={{ border: 'solid 1px #ced4da', padding: 16 }}>
            <AppStack style={{ gap: 12 }}>
              <AppFlexbox
                style={{
                  gap: rs([12, 12, 24], mqIndex),
                  flexDirection: rs(['column', 'column', 'row'], mqIndex),
                  alignItems: 'flex-start'
                }}
              >
                <AppStack
                  style={{
                    gap: 6,
                    alignSelf: 'stretch',
                    minWidth: rs(['unset', 'unset', 230], mqIndex)
                  }}
                >
                  {summaryRows.map((r) => (
                    <AppFlexbox
                      key={r.label}
                      style={{ gap: 20, justifyContent: 'space-between' }}
                    >
                      <AppText style={{ fontSize: 13, color: '#666' }}>
                        {r.label}
                      </AppText>
                      <AppText
                        style={{
                          fontSize: 13,
                          color: r.color,
                          whiteSpace: 'nowrap'
                        }}
                        weight={500}
                      >
                        {r.value}
                      </AppText>
                    </AppFlexbox>
                  ))}

                  {refreshError && refreshError.status !== 404 && (
                    <AppText style={{ fontSize: 12, color: '#666' }}>
                      We couldn&apos;t reach the payment provider just now.
                    </AppText>
                  )}
                </AppStack>

                <AppStack
                  style={{
                    flex: 1,
                    gap: 6,
                    minWidth: 0,
                    alignSelf: 'stretch'
                  }}
                >
                  <AppText style={{ fontSize: 13, color: '#666' }}>
                    Scheduled payments
                  </AppText>
                  <PaymentPlanInstallmentTable
                    installments={plan.installments}
                  />
                </AppStack>
              </AppFlexbox>

              <Divider />

              <PaymentPlanOrderSummary
                isLoading={isOrderLoading}
                pkRegCart={plan.pkRegCart}
                regCart={orderRegCart}
              />

              <Divider />

              {summary.isCompleted ? (
                <AppText style={{ fontSize: 14 }} weight={500}>
                  {summary.isFullyPaid
                    ? 'This payment plan is fully paid.'
                    : 'This payment plan is closed. No further payments are scheduled.'}
                </AppText>
              ) : !plan.isPaymentMethodUpdateSupported ? (
                <AppText style={{ fontSize: 13, color: '#666' }}>
                  Payment method updates are not available for this order.
                  Please contact {plan.associationName ?? 'your association'}.
                </AppText>
              ) : (
                <AppFlexbox style={{ gap: 12, alignItems: 'center' }}>
                  <Button
                    color="blue"
                    onClick={() => setModalState({ isOpen: true })}
                    size="compact-md"
                  >
                    Update card details
                  </Button>
                </AppFlexbox>
              )}
            </AppStack>
          </AppCard>
        </>
      )}
    </HeaderView>
  );
};

export default PaymentPlanDetails;
